/**
 * @description Interfaces, enums and types related to data connectors.
 * @author Michal Drewniak (michal@blaze.tech)
 */

import { type SetOptional } from 'type-fest';

import {
  type ConnectorInfo,
  type ConnectorTarget,
  type ConnectorType,
  type DatasetInfo,
} from '@blaze-shared/api-contracts-domain';
import { type OAuthAuthorizationMessage } from '@blaze-shared/auth-domain';
import { type DatasetSchema } from '@blaze-shared/schema-domain';

import { type FormConfig, type FormValues } from './connector-form.model';

export interface ApiAction {
  name: string;
  parameters?: ApiActionParameter[];
}

export interface ApiActionParameter {
  name: string;
  isRequired: boolean;
}

export interface ConnectorConfiguration<T extends Record<string, any> = FormValues>
  extends SetOptional<ConnectorInfo, 'webhooks' | 'webhookWorkflows'> {
  configuration: T;
  projectId?: string; // Set this property to make a connector project scoped.
}

export interface ConnectorTemplate {
  className: string;
  configuration: {
    configurationType: ConfigurationType;
    form: FormConfig;
    parameters: ConnectorTemplateParameter[];
  };
  id: string;
  target: ConnectorTarget;
  displayName?: string;
  connectorType: ConnectorType;
}

export interface ConnectorTestResult {
  accountId?: string;
  actions?: ApiAction[];
  configuration?: FormValues;
  connectorId?: string;
  datasetName?: string;
  datasets?: DatasetSchema[];
  templateId?: string;
}

export interface EditedConnectorConfiguration {
  name: string;
  configuration?: FormValues;
  datasets?: DatasetInfo[];
}

export interface EditedConnectorTestResult {
  availableDatasets?: DatasetSchema[];
  connector?: ConnectorConfiguration;
  hasChanges: boolean;
  previouslySelectedDatasets?: DatasetSchema[];
}

export type ConfigurationType = 'Parameters';

export type ConnectorTypeREMOVEME = 'Api' | 'DataSource' | 'CustomApi';

export interface ConnectorTemplateParameter {
  name: string;
  dataType: string | number;
  description: string;
  isRequired: boolean;
}

export interface ConnectorCreationRequest {
  payload: Record<string, unknown>;
  projectId?: string;
  target: string;
}

export interface AirtableConnectorCreationRequest {
  accountId: string;
  name: string;
  apiKey: string;
  baseId: string;
  tables: AirtableTableDefinition[];
  metadataSampleSize?: number;
}

export interface AirtableTableDefinition {
  name: string;
  primaryFieldName?: string;
  tableId?: string | null;
}

export interface AirtableConnectorResponse {
  isSuccess: boolean;
  connectorInfo: ConnectorConfiguration<AirtableConnectorConfiguration>;
  errors?: string[] | null;
}

export type AirtableConnectorUpdateRequest = Pick<
  AirtableConnectorCreationRequest,
  'name' | 'apiKey' | 'tables' | 'baseId'
>;

export interface AirtableConnectorConfiguration {
  apiKey: string;
  baseId: string;
  tables: {
    Name: string;
    PrimaryFieldName?: string | null;
    TableId?: string | null;
  }[];
}

export enum ConnectorSetupPhase {
  Init,
  Authorization,
  PopupOpened,
  Install,
  ConnectorCreation,
}

export interface ConnectorOAuthConfig {
  target: string;
  serviceName: string;
  urlName: string;

  /**
   * Filters fields that are returned after successful authorization.
   */
  allowedInstallFields?: string[];

  /**
   * What to do after successful authorization and filling up the form.
   */
  actionAfterDefinition?: 'close' | 'showDatasets';
}

export interface GenericOAuthAuthorizationMessage extends OAuthAuthorizationMessage {
  [key: string]: any;
}

export interface ServiceAuthorizationMessage extends OAuthAuthorizationMessage {
  authuser: string;
  code: string;
  hd: string;
  prompt: string;
  scope: string;
  state: string;
}

export const connectorServiceIcons: Record<ConnectorTarget, string> = Object.freeze({
  Aircall: 'icon_datasource_aircall',
  Airtable: 'icon_datasource_airtable',
  Basiq: 'icon_datasource_basiq',
  BigQuery: 'icon_datasource_bigquery',
  BlazeFunctions: 'icon_field_type_formula_16',
  BlazeMail: 'icon_datasource_blazemail',
  BlazeTable: 'icon_blaze_table_32',
  BlazeTableLinked: 'icon_blaze_table_linked_32',
  BlazeTextMessaging: 'icon_datasource_blazetextmsg',
  'Cal.com': 'icon_datasource_cal',
  Calendly: 'icon_datasource_calendly',
  ConnectorSelector: 'icon_datasource_connectionselector',
  DocuSign: 'icon_datasource_docusign',
  Dropbox: 'icon_datasource_dropbox',
  DropboxSign: 'icon_datasource_dropboxsign',
  DynamoDB: 'icon_datasource_dynamodb',
  Foxy: 'icon_datasource_foxy',
  Freshdesk: 'icon_datasource_freshdesk',
  GoogleAnalytics: 'icon_datasource_googleanalytics',
  GoogleCalendar: 'icon_datasource_googlecalendar',
  GoogleMail: 'icon_datasource_googlemail',
  GoogleSheets: 'icon_datasource_googlesheets',
  InBody: 'icon_datasource_in_body',
  Kareo: 'icon_datasource_kareo',
  LifeFile: 'icon_datasource_lifefile',
  MailGun: 'icon_datasource_mailgun',
  MariaDB: 'icon_datasource_mariadb',
  Marketo: 'icon_datasource_marketo',
  MicrosoftTeams: 'icon_datasource_ms_teams',
  MongoDB: 'icon_datasource_mongodb',
  MySQL: 'icon_datasource_mysql',
  NMI: 'icon_dummy_32',
  NextCloud: 'icon_datasource_nextcloud',
  OneDrive: 'icon_datasource_onedrive',
  OpenAI: 'icon_datasource_openai',
  PayPal: 'icon_datasource_paypal',
  Plaid: 'icon_datasource_plaid',
  PlaidIdentityVerification: 'icon_datasource_plaid',
  PioneerRx: 'icon_datasource_pioneer_rx',
  PostgreSQL: 'icon_datasource_postgresql',
  QuickBooks: 'icon_datasource_quickbooks',
  RestApi: 'icon_datasource_rest_api',
  S3: 'icon_datasource_amazon_s3',
  Salesforce: 'icon_datasource_salesforce',
  ScriptSure: 'icon_datasource_scriptsure',
  SendGrid: 'icon_datasource_sendgrid',
  Shopify: 'icon_datasource_shopify',
  Snowflake: 'icon_datasource_snowflake',
  SqlServer: 'icon_datasource_sqlserver',
  Square: 'icon_datasource_square',
  Stripe: 'icon_datasource_stripe',
  Twilio: 'icon_datasource_twilio',
  Typeform: 'icon_datasource_typeform',
  UpKeep: 'icon_datasource_upkeep',
} as Record<ConnectorTarget, string>);

export type AuthorizeUrlSpecification =
  | { googleServiceType: GoogleServiceType }
  | { serviceType: MicrosoftServiceType };

//#region Google
export enum GoogleServiceType {
  Analytics = 'Analytics',
  Calendar = 'Calendar',
  Spreadsheets = 'Spreadsheets',
  Mail = 'Mail',
}

export interface GoogleSheetDef {
  id: string;
  name: string;
}

export interface GoogleCalendarDef {
  id: string;
  description: string | null;
  location: string | null;
  summary: string;
  timeZone: string;
}

//#endregion

//#region Microsoft
export enum MicrosoftServiceType {
  Messaging = 'Messaging',
  OneDrive = 'OneDrive',
}

export interface OneDriveDef {
  id: string;
  description: string;
  name: string;
}
//#endregion

//#region Typeform
// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace Typeform {
  export interface FormsInfoResponse {
    formInfos: FormInfo[];
    page: number;
  }

  export interface FormInfo {
    id: string;
    isPublic: boolean;
    isTrial: boolean;
    title: string;
    type: string;
  }

  export interface FormSchema {
    id: string;
    title: string;
    type: string;
    allowMultipleSelection: boolean;
    allowedColumnIds: string[];
    fields: string[];
  }
}
//#endregion
